
import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";
import { SystemLog } from "@/pages/tech/SystemMonitor";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

interface ErrorLogsDisplayProps {
  data: SystemLog[] | undefined;
  isLoading: boolean;
  onNewLog?: (log: SystemLog) => void;
}

const ErrorLogsDisplay: React.FC<ErrorLogsDisplayProps> = ({ 
  data, 
  isLoading, 
  onNewLog 
}) => {
  // Filter out information logs - only show errors and warnings
  const [showInfoLogs, setShowInfoLogs] = useState(false);
  
  const filteredLogs = data?.filter(log => 
    showInfoLogs ? true : (log.level === 'error' || log.level === 'warning')
  );

  useEffect(() => {
    const channel = supabase
      .channel('error-logs-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'system_logs',
          filter: 'level=eq.error',
        },
        (payload) => {
          console.log('New error log received:', payload);
          if (onNewLog && payload.new) {
            onNewLog(payload.new as SystemLog);
            toast({
              title: "New Error Detected",
              description: payload.new.message,
              variant: "destructive",
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [onNewLog]);

  return (
    <Card className="bg-white">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-xl font-title text-primary">Recent Error Logs</CardTitle>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => setShowInfoLogs(!showInfoLogs)}
          className="text-xs"
        >
          {showInfoLogs ? "Hide Info Logs" : "Show All Logs"}
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-28 w-full" />
            ))}
          </div>
        ) : filteredLogs && filteredLogs.length > 0 ? (
          <div className="space-y-4">
            {filteredLogs.map((error) => (
              <Card key={error.id} className={`border-l-4 ${
                error.level === 'error' ? 'border-l-red-500' : 
                error.level === 'warning' ? 'border-l-amber-500' : 
                error.status === 'unresolved' ? 'border-l-blue-500' : 
                error.status === 'investigating' ? 'border-l-amber-500' : 
                'border-l-green-500'
              }`}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <p className="font-mono text-sm font-medium">{error.id.substring(0, 8)}</p>
                      <Badge variant={
                        error.status === 'unresolved' ? 'destructive' : 
                        error.status === 'investigating' ? 'warning' : 
                        'success'
                      } className={
                        error.status === 'unresolved' ? 'bg-red-500' : 
                        error.status === 'investigating' ? 'bg-amber-500' : 
                        'bg-green-500'
                      }>
                        {error.status.charAt(0).toUpperCase() + error.status.slice(1)}
                      </Badge>
                      <Badge variant={
                        error.level === 'error' ? 'destructive' : 
                        error.level === 'warning' ? 'warning' : 
                        'outline'
                      } className={
                        error.level === 'error' ? 'bg-red-500' : 
                        error.level === 'warning' ? 'bg-amber-500' : 
                        'bg-blue-500 text-white'
                      }>
                        {error.level.charAt(0).toUpperCase() + error.level.slice(1)}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground font-caption">
                      {new Date(error.timestamp).toLocaleString()}
                    </p>
                  </div>
                  <p className="font-body text-sm mb-2">{error.message}</p>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-3.5 w-3.5 text-primary" />
                      <p className="text-xs text-muted-foreground">Component: {error.component}</p>
                    </div>
                    <Button variant="outline" size="sm" className="h-7 text-xs">View Details</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-muted-foreground">
              {showInfoLogs ? "No logs found" : "No errors or warnings found"}
            </p>
            {!showInfoLogs && data && data.length > 0 && (
              <Button 
                variant="link" 
                size="sm" 
                onClick={() => setShowInfoLogs(true)}
                className="mt-2"
              >
                Show all {data.length} logs
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ErrorLogsDisplay;
